import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, FileWarning } from 'lucide-react'
import { getDraftFile, putDraftFile } from '@/api/draftFiles'
import { getDraftPrompt } from '@/api/prompts'
import { Banner } from '@/components/Banner'
import { ConfirmModal } from '@/components/ConfirmModal'
import { EmptyState } from '@/components/EmptyState'
import { errorMessage } from '@/lib/apiErrors'
import { refreshAfterWrite } from '@/lib/refreshAfterWrite'
import type { DocumentTypeEntry, DocumentTypesFile } from '@/types/bundle'
import { documentTypeLabel } from './labels'
import { DocumentTypesTable, type DocumentTypeRow } from './components/DocumentTypesTable'
import { NewOrDuplicateDocumentTypeModal } from './components/NewOrDuplicateDocumentTypeModal'

function SkeletonBlock() {
  return <div className="h-48 animate-pulse rounded-control bg-surface-raised" />
}

export function DocumentTypesListScreen() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const documentTypesQuery = useQuery({
    queryKey: ['draft-file', 'document_types'],
    queryFn: () => getDraftFile<DocumentTypesFile>('document_types'),
  })

  const [duplicateSource, setDuplicateSource] = useState<{ id: string; entry: DocumentTypeEntry } | undefined>(
    undefined,
  )
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<DocumentTypeRow | null>(null)

  // Deleting from this list screen is immediate (confirm -> PUT), not staged
  // behind a Save button - same posture as Product Types' own list-screen
  // delete (no other draft/edit state exists on this screen to stage a
  // removal alongside).
  const deleteMutation = useMutation({
    mutationFn: (payload: DocumentTypesFile) => putDraftFile<DocumentTypesFile>('document_types', payload),
    onSuccess: () => {
      refreshAfterWrite(queryClient, { kind: 'draft-file', fileKey: 'document_types' })
    },
  })

  if (documentTypesQuery.isPending) {
    return <SkeletonBlock />
  }

  if (documentTypesQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load document types">
        {errorMessage(documentTypesQuery.error)}
      </Banner>
    )
  }

  const documentTypesFile = documentTypesQuery.data
  const documentTypes = documentTypesFile.document_types
  const documentTypeIds = Object.keys(documentTypes)

  const rows: DocumentTypeRow[] = documentTypeIds.map((id) => ({ id, entry: documentTypes[id] }))

  function openNewModal() {
    setDuplicateSource(undefined)
    setModalOpen(true)
  }

  function openDuplicateModal(row: DocumentTypeRow) {
    setDuplicateSource({ id: row.id, entry: row.entry })
    setModalOpen(true)
  }

  // Duplicate's source Document Prompt content (if any) is fetched here,
  // before navigating, and seeded into the new (unsaved) form via router
  // state - written to a freshly-derived file only when the operator
  // actually saves there (Design PRD 10's Duplicate bullet). Best-effort: a
  // fetch failure just means the new form starts empty rather than blocking
  // Duplicate entirely.
  async function handleConfirm(newId: string, initialEntry: DocumentTypeEntry) {
    const sourceFilename = duplicateSource?.entry.prompt?.template_file
    let initialDocumentPromptContent: string | undefined
    if (sourceFilename) {
      try {
        const prompt = await getDraftPrompt(sourceFilename)
        initialDocumentPromptContent = prompt.content
      } catch {
        initialDocumentPromptContent = undefined
      }
    }
    navigate(`/document-types/${newId}`, { state: { initialEntry, initialDocumentPromptContent } })
  }

  function handleConfirmDelete() {
    if (!deleteTarget) return
    const nextDocumentTypes = { ...documentTypes }
    delete nextDocumentTypes[deleteTarget.id]
    deleteMutation.mutate(
      { ...documentTypesFile, document_types: nextDocumentTypes },
      { onSuccess: () => setDeleteTarget(null) },
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-title font-semibold text-text-primary">Documents</h1>
        <button
          type="button"
          onClick={openNewModal}
          className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white"
        >
          + New Document Type
        </button>
      </div>

      {deleteMutation.isError && (
        <Banner variant="danger" icon={AlertCircle} title="Delete failed">
          {errorMessage(deleteMutation.error)}
        </Banner>
      )}

      {documentTypeIds.length === 0 ? (
        <EmptyState
          icon={FileWarning}
          headline="No document types defined yet"
          description="Add a document type to start matching and extracting documents against it."
          action={{ label: '+ New Document Type', onClick: openNewModal }}
        />
      ) : (
        <DocumentTypesTable rows={rows} onDuplicate={openDuplicateModal} onDelete={setDeleteTarget} />
      )}

      <NewOrDuplicateDocumentTypeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        existingIds={documentTypeIds}
        source={duplicateSource}
        onConfirm={handleConfirm}
      />

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete document type"
        description={`"${deleteTarget ? documentTypeLabel(deleteTarget.id) : ''}" will be removed immediately - there is no separate Save step on this screen. Make sure no product type's Document requirements still needs it, and it's not referenced elsewhere first.`}
        confirmLabel={deleteMutation.isPending ? 'Deleting…' : 'Delete'}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
